import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
} from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import RuleFolderIcon from "@mui/icons-material/RuleFolder";
import SettingsIcon from "@mui/icons-material/Settings";

import { PERMISSIONS } from "../../constants";
import HubGrid, { HUB_ICON_SX, HubNoAccess } from "./HubGrid";
import { canSeeApplicants, hasBothBranches, isExtensionAdmin } from "./extensionAccess";
import { allApplications, getApplicantConfig } from "../../services/backend/ApplicantConfigService";

// Rama de Aspirantes dentro de Extensión: todo lo que tiene que ver con el banco
// de hojas de vida.
export default function ApplicantsHub() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  // Catálogo de convocatorias — decide si el mosaico "Por convocatoria" aporta
  // algo. Sólo lo necesita esta rama, así que la lectura vive aquí y no en el
  // hub de arriba, que ahora no consulta nada.
  const [calls, setCalls] = useState([]);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const allowed = canSeeApplicants(user);

  useEffect(() => {
    if (!allowed) {
      setConfigLoaded(true);
      return undefined;
    }
    let alive = true;
    getApplicantConfig().then((cfg) => {
      if (!alive) return;
      // General primero (allApplications) y las inactivas al final: un gestor
      // puede seguir necesitando revisar los postulados de una ya cerrada.
      const sorted = allApplications(cfg)
        .slice()
        .sort((a, b) => (a.active === false ? 1 : 0) - (b.active === false ? 1 : 0));
      setCalls(sorted);
      setConfigLoaded(true);
    });
    return () => { alive = false; };
  }, [allowed]);

  const openCall = useCallback((id) => {
    setPickerOpen(false);
    navigate(`/applicants?call=${encodeURIComponent(id)}`);
  }, [navigate]);

  const options = useMemo(() => {
    const perms = user?.permissions || [];
    const isAdmin = isExtensionAdmin(user);
    const items = [];
    if (perms.includes(PERMISSIONS.APPLICANT.LIST) || isAdmin) {
      items.push({
        key: "applicants",
        label: "Todos los aspirantes",
        description: "Banco de postulantes: consulta y busca hojas de vida.",
        icon: <PeopleAltIcon sx={HUB_ICON_SX} />,
        path: "/applicants",
      });
    }
    // Sólo aporta algo cuando hay convocatorias además de General.
    if ((perms.includes(PERMISSIONS.APPLICANT.LIST) || isAdmin) && calls.length > 1) {
      items.push({
        key: "by-call",
        label: "Por convocatoria",
        description: "Filtra los aspirantes postulados a una convocatoria.",
        icon: <CampaignIcon sx={HUB_ICON_SX} />,
        onClick: () => setPickerOpen(true),
      });
    }
    if (perms.includes(PERMISSIONS.APPLICANT.VALIDATE_DOC) || isAdmin) {
      items.push({
        key: "validar",
        label: "Validar documentos",
        description: "Revisa y valida la documentación de los aspirantes.",
        icon: <RuleFolderIcon sx={HUB_ICON_SX} />,
        path: "/applicants?status=COMPLETE",
      });
    }
    if (isAdmin) {
      items.push({
        key: "config",
        label: "Configuración",
        description: "Documentos exigibles, tipos de identificación, niveles y almacenamiento.",
        icon: <SettingsIcon sx={HUB_ICON_SX} />,
        path: "/applicants/config",
      });
    }
    return items;
  }, [user, calls]);

  // Se espera al catálogo: redirigir antes de que cargue podría saltarse un
  // mosaico que el usuario sí tiene.
  useEffect(() => {
    if (configLoaded && options.length === 1 && options[0].path) {
      navigate(options[0].path, { replace: true });
    }
  }, [configLoaded, options, navigate]);

  if (!allowed) {
    return (
      <HubNoAccess
        title="Sin acceso a Aspirantes"
        message="Tu usuario no tiene permisos para el banco de aspirantes."
      />
    );
  }
  if (!configLoaded) return null;
  if (options.length === 1) return null;

  return (
    <HubGrid
      title="Aspirantes"
      subtitle="Selecciona una opción"
      options={options}
      backTo={hasBothBranches(user) ? "/extension/inicio" : null}
      backLabel="Extensión"
    >
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Elige una convocatoria</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List disablePadding>
            {calls.map((c) => (
              <ListItemButton key={c.id} onClick={() => openCall(c.id)}>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{c.name || c.id}</span>
                      {c.active === false && <Chip size="small" label="Inactiva" />}
                    </Stack>
                  }
                  secondary={c.id}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </HubGrid>
  );
}
